import { useEffect, useMemo, useRef } from 'react';
import cx from 'classnames';
import { postServiceInfo } from 'govbr.frontend.institucional/actions';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import ServiceCard from './ServiceCard';
import servicesStyles from './ServicesBody.module.less';
import { getSanitizedUrlFromEntry } from './servicesUtils';

const messages = defineMessages({
  loading: {
    id: 'Loading services...',
    defaultMessage: 'Loading services...',
  },
  noServices: {
    id: 'No services to display.',
    defaultMessage: 'No services to display.',
  },
  noResults: {
    id: 'No services were found for the provided URLs.',
    defaultMessage: 'No services were found for the provided URLs.',
  },
  invalidUrl: {
    id: 'Invalid service URL: {url}',
    defaultMessage: 'Invalid service URL: {url}',
  },
  error: {
    id: 'Unable to load services. Try again later.',
    defaultMessage: 'Unable to load services. Try again later.',
  },
});

const ensureUniqueUrls = (serviceUrls = []) => {
  const unique = [];
  const seen = new Set();

  serviceUrls.forEach((entry) => {
    const candidate = getSanitizedUrlFromEntry(entry);
    const normalized = typeof candidate === 'string' ? candidate.trim() : '';

    if (normalized && !seen.has(normalized)) {
      unique.push(normalized);
      seen.add(normalized);
    }
  });

  return unique;
};

const ServicesBody = (props) => {
  const { data = {}, isEditMode = false, block, id } = props;
  const intl = useIntl();
  const dispatch = useDispatch();

  const servicesState = useSelector((state) => state.services) || {};
  const blockId = block || id || '';
  const subrequests = servicesState.subrequests || {};
  const requestState = blockId ? subrequests[blockId] : null;
  const {
    loading = false,
    loaded = false,
    error = null,
    data: servicesData = {},
  } = requestState || servicesState;

  const urls = useMemo(
    () => ensureUniqueUrls(data.serviceUrls),
    [data.serviceUrls],
  );

  const requestKey = useMemo(() => urls.join('|'), [urls]);
  const lastRequestRef = useRef({ key: '', blockId: blockId || '' });

  useEffect(() => {
    lastRequestRef.current = { key: '', blockId: blockId || '' };
  }, [blockId]);

  useEffect(() => {
    if (urls.length === 0) {
      lastRequestRef.current.key = '';
      return;
    }

    if (requestKey !== lastRequestRef.current.key) {
      dispatch(postServiceInfo(urls, blockId));
      lastRequestRef.current.key = requestKey;
    }
  }, [blockId, dispatch, requestKey, urls]);

  const validServices = useMemo(() => {
    if (!servicesData || typeof servicesData !== 'object') return [];

    return urls
      .map((url) => {
        const info = servicesData?.[url];
        if (!info) return null;

        return {
          url,
          categoria: info.categoria || info.category,
          titulo: info.titulo || info.title,
          title: info.title,
          descricao: info.descricao || info.description,
          description: info.descricao || info.description,
        };
      })
      .filter(Boolean);
  }, [urls, servicesData]);

  const invalidUrls = useMemo(() => {
    if (!loaded) return [];
    if (!servicesData || typeof servicesData !== 'object') {
      return urls;
    }
    return urls.filter((url) => !servicesData[url]);
  }, [loaded, urls, servicesData]);

  const renderHeading = () => {
    const { title, subtitle } = data;
    if ((!title && !subtitle) || validServices.length <= 0) return null;

    return (
      <div className="text-center mb-4">
        {title ? <h2 className="sectionTitle">{title}</h2> : null}
        {subtitle ? (
          <h3 className="section-subtitle">
            <span>{subtitle}</span>
          </h3>
        ) : null}
      </div>
    );
  };

  const renderLoading = () => (
    <div className="text-center p-4 text-muted">
      {intl.formatMessage(messages.loading)}
    </div>
  );

  const renderError = () => {
    if (!error) return null;

    const errorMessage =
      typeof error === 'string'
        ? error
        : error?.message || intl.formatMessage(messages.error);

    return (
      <div className="feedback alert" role="alert">
        {errorMessage}
      </div>
    );
  };

  const renderEmptyState = () => {
    if (urls.length > 0 || !isEditMode || loading) return null;

    return (
      <div className="text-center">
        {intl.formatMessage(messages.noServices)}
      </div>
    );
  };

  const renderNoResults = () => {
    if (loading || !loaded || urls.length === 0) return null;
    if (error) return null;
    if (validServices.length > 0) return null;

    return (
      <div className="text-center">
        {intl.formatMessage(messages.noResults)}
      </div>
    );
  };

  const renderInvalidUrls = () => {
    if (!isEditMode || invalidUrls.length === 0) return null;

    return (
      <div className="mt-3">
        {invalidUrls.map((url) => (
          <div key={url} className="text-center feedback warning" role="alert">
            {intl.formatMessage(messages.invalidUrl, { url })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mb-4">
      {renderHeading()}

      {loading ? renderLoading() : null}
      {renderError()}

      {!loading && validServices.length > 0 ? (
        <div className={cx('my-5', servicesStyles.colsServices)}>
          {validServices.map((service) => (
            <div className={servicesStyles.wrapper} key={service.url}>
              <ServiceCard service={service} isEditMode={isEditMode} />
            </div>
          ))}
        </div>
      ) : null}

      {renderEmptyState()}
      {renderNoResults()}
      {renderInvalidUrls()}
    </div>
  );
};

ServicesBody.propTypes = {
  data: PropTypes.object,
  isEditMode: PropTypes.bool,
  block: PropTypes.string,
  id: PropTypes.string,
};

export default ServicesBody;
